/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { isEqual } from 'lodash'

import {
  Button,
  Heading,
  FileView,
  FileMetaData,
  SvgIcon
} from '../'

import {
  isViewableFileFormat,
  getSldsIconType,
  getFileExtension,
  hasResponsibility
} from '../../utils'

import './file-viewer.css'


export default class FileViewer extends React.Component {
  static propTypes = {
    heading: PropTypes.string,
    files: PropTypes.arrayOf(PropTypes.shape({
      asset: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired
    })).isRequired,
    fileActions: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      action: PropTypes.func.isRequired
    })),
    selectFile: PropTypes.string,
    inline: PropTypes.bool,
    onClick: PropTypes.func,
    preview: PropTypes.bool,
    showMetaData: PropTypes.bool,
    displayFileChildren: PropTypes.bool,
    displayEmail: PropTypes.func,
    className: PropTypes.string
  }

  constructor(props) {
    super(props)

    this.state = {
      fileSelected: props.preview
        ? props.selectFile
          ? this.getFile(props.selectFile)
          : props.files[0]
        : null
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!isEqual(nextProps.files, this.props.files)) {
      this.setState({
        fileSelected: nextProps.files.length > 0
          ? nextProps.files[0]
          : null
      })
    }
    if (nextProps.selectFile !== this.props.selectFile) {
      this.setState({
        fileSelected: nextProps.selectFile
          ? this.getFile(nextProps.selectFile)
          : null
      })
    }
  }

  getFile = (id) => (
    this.props.files.find(file => file.id === id)
  )

  handleFileSelected = (file) => {
    this.setState({ fileSelected: file })
    if (this.props.onClick) {
      this.props.onClick(file.id)
    }
  }

  handleFileDownload = (file) => {
    let filenameToSaveAs = file.asset.split('__').pop()

    let a = document.createElement('a')
    a.href = file.url
    a.download = filenameToSaveAs  // TODO: make sure rename happens (currently only honored for links to resources with the same-origin)
    a.onclick = (e) => document.body.removeChild(e.target)
    a.style.display = 'none'
    document.body.appendChild(a)

    a.click()
  }

  render() {
    return (
      <React.Fragment>
        {this.props.showMetaData && this.state.fileSelected &&
          <FileMetaData
            file={this.state.fileSelected.id}
            inverseColours={true}
            onSourceClick={this.props.displayEmail}
          />
        }
        {this.props.files.length === 0
          ? <div className='u-font-size-larger slds-text-color_inverse slds-grid slds-align_absolute-center' style={{ height: '300px' }}>
              <SvgIcon
                variant='utility'
                symbol='info'
                colour='inverse'
                size='small'
                className='slds-m-right_small'
              />
              No files to display.
            </div>
          : <div className='slds-scrollable_y slds-m-around_medium'>
              <div className='slds-p-bottom_small'>
                {this.props.heading &&
                  <Heading className='slds-p-around_small' marginBottom={5}>
                    {this.props.heading}
                  </Heading>
                }
                <FileThumbnails
                  files={this.props.files}
                  onFileSelected={this.handleFileSelected}
                  displayChildren={this.props.displayFileChildren}
                />
              </div>
                <div>
                  <FilePreview
                    file={this.state.fileSelected}
                    onFileDownloadClick={this.handleFileDownload}
                    fileActions={this.props.fileActions}
                    inline={this.props.inline}
                  />
                </div>
            </div>
        }
      </React.Fragment>
    )
  }
}

class FileThumbnails extends React.Component {
  static propTypes = {
    files: PropTypes.arrayOf(PropTypes.shape({
      asset: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired
    })).isRequired,
    onFileSelected: PropTypes.func.isRequired,
    displayChildren: PropTypes.bool
  }

  static defaultProps = {
    displayChildren: true
  }

  getFileThumbnails = (files, onFileSelected) => (
    files.map((file, index) => (
      <span className='thumbnail slds-m-around_small' key={`thumbnail_${index}`}>
        <span className='thumbnail_grouping'>
          {this.displayThumbnail(file, onFileSelected)}
          {this.props.displayChildren
            ? this.displaySubThumbnails(file, onFileSelected)
            : null
          }
        </span>
      </span>
    ))
  )

  displayThumbnail = (file, onFileSelected, index) => {
    let fileExtension = getFileExtension(file.asset)

    if (isViewableFileFormat(fileExtension)) {
      return (
        <span className='slds-p-around_small'>
          <Button
            type='reset'
            onClick={() => onFileSelected(file)}
            key={`${index ? `${file}_sub-thumbnail_${index}` : ''}`}>
            <img
              src={file.url}
              alt=''
            />
          </Button>
        </span>
      )
    } else {
      return (
        <Button
          type='reset'
          onClick={() => onFileSelected(file)}>
          <span className='slds-icon_container slds-p-around_medium' title={fileExtension}>
            <SvgIcon
              variant='doctype'
              symbol={getSldsIconType(fileExtension)}
              size='large'
            />
            <span className='slds-assistive-text'>{fileExtension}</span>
          </span>
        </Button>
      )
    }
  }

  displaySubThumbnails = (file, onFileSelected) => {
    if (this.hasSubThumbnails(file)) {
      let subThumbnails = this.getSubThumbnails(file)

      return subThumbnails.map((thumbnail, index) => (
        this.displayThumbnail(thumbnail, onFileSelected, index)
      ))
    }
  }

  hasSubThumbnails = (file) => file.children.edges.length > 0
  getSubThumbnails = (file) => file.children.edges.map(child => child.node)

  filesHasMoreThanOneThumbnail = () => {
    let filesThumbnails = this.props.files.length  // there'll be at least the number of thumbnails as there are files
    this.props.files.forEach(file => {
      if (this.hasSubThumbnails(file)) {
        filesThumbnails++  // an increment of one per sub-thumbnail is sufficient as any addition will cause this function to return true
      }
    })

    return filesThumbnails > 1
  }

  render() {
    if (this.filesHasMoreThanOneThumbnail()) {
      return (
        <div className='thumbnailPreview'>
          <div className='slds-size_1-of-1 slds-p-around_medium slds'>
            {this.getFileThumbnails(this.props.files, this.props.onFileSelected)}
          </div>
        </div>
      )
    } else {
      return null
    }
  }
}

export class FilePreview extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      download: true,
      startLoanApplication: null,
      attachFile: null
    }
  }

  getFileActions = () => {
    if (!this.props.inline) {
      return (
        <Button
          onClick={() => this.props.onFileDownloadClick(this.props.file)}>
          Download
        </Button>
      )
    } else if (this.props.fileActions) {
      return this.props.fileActions.map((fileAction, index) => {
        if (fileAction.name === 'download') {
          return (
            <div key={`filepreview_action_${fileAction.name}_${index}`}>
              {this.state.download
                ? <Button
                    onClick={() => this.props.onFileDownloadClick(this.props.file)}>
                    Download
                  </Button>
                : null
              }
            </div>
          )
        }

        if (fileAction.name === 'loanapplication' && !this.state.attachFile) {
          if (hasResponsibility('self', 'loanapplication_create', 'client', null)) {
            return (
              <div key={`filepreview_action_${fileAction.name}_${index}`}>
                {this.state.startLoanApplication
                  ? this.state.startLoanApplication
                  : <Button
                      onClick={() =>
                        this.setState({
                          startLoanApplication: fileAction.action('loanapplication', null),
                          download: false,
                          attachFile: false
                        })}>
                      Start loan application
                    </Button>
                }
              </div>
            )
          }
        }

        if (fileAction.name === 'attach' && !this.state.startLoanApplication) {
          return (
            <div key={`filepreview_action_${fileAction.name}_${index}`}>
              {this.state.attachFile
                ? this.state.attachFile
                : <Button
                    onClick={() =>
                      this.setState({
                        attachFile: fileAction.action('attach', null),
                        startLoanApplication: false,
                        download: false
                      })}>
                    Attach file to client/employer
                  </Button>
              }
            </div>
          )
        }

        return <span key={`filepreview_action_${fileAction.name}_${index}`}></span>
      })
    }
  }

  render() {
    return (
      <div className='view'>
        {this.props.file
          ? <div className='slds-scrollable_y'>
              <div className='slds-p-bottom_large'>
                <FileView file={this.props.file} />
              </div>
              <div className='slds-p-around_medium'>
                <div className='slds-align_absolute-center'>
                  {this.getFileActions()}
                </div>
              </div>
            </div>
          : <span className='slds-p-around_large slds-align_absolute-center'>
              Click on a file to preview its contents or download
            </span>
        }
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/file-viewer/index.js